import React from "react";
import * as Sentry from "@sentry/react";
import { ReactComponent as BossLogo } from "./svgs/logo-to-edge.svg";
import "./ErrorBoundary.css";
import { RELOAD_WAIT_MS, isReloadingForNewBuild } from "./staleBuild";

// Each deploy replaces the hashed page files. An open tab that navigates after a
// deploy asks for a file that no longer exists. Browsers word that differently,
// and Safari reports the HTML a host answers with as a MIME type error.
const isPageDownloadError = (error) =>
  /ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload|is not a valid JavaScript MIME type|disallowed MIME type/i.test(
    `${error?.name || ""} ${error?.message || ""}`,
  );

// Keep recovery independent of routing, application providers, and page bundles:
// it must still render when any of those fail to load or render.
class ErrorBoundary extends React.Component {
  // While a stale tab loads a new page in full, a download failure is expected.
  // Then `holding` is true: the boundary shows nothing and reports nothing yet.
  state = { error: null, holding: false };
  holdTimer = null;
  // The fallback only exists after commit, so focus the heading as it mounts.
  focusHeading = (heading) => heading?.focus();

  static getDerivedStateFromError(error) {
    return { error, holding: isPageDownloadError(error) && isReloadingForNewBuild() };
  }

  componentDidCatch(error, errorInfo) {
    if (!this.state.holding) {
      this.report(error, errorInfo);
      return;
    }
    // If the full load does not happen, the page stays broken: show and report it then.
    this.holdTimer = setTimeout(() => {
      this.holdTimer = null;
      this.report(error, errorInfo);
      this.setState({ holding: false });
    }, RELOAD_WAIT_MS);
  }

  componentWillUnmount() {
    clearTimeout(this.holdTimer);
  }

  report(error, errorInfo) {
    const downloadFailed = isPageDownloadError(error);
    // Reporting is best effort; a broken reporter must not break recovery.
    try {
      this.props.onError?.(error, errorInfo);
      // Sentry is only initialised in production builds.
      if (Sentry.getClient()) {
        // Group download failures as one issue, not one issue per page file.
        const downloadContext = downloadFailed
          ? { level: "warning", fingerprint: ["recovery-screen", "page-download"] }
          : {};
        Sentry.captureException(error, {
          ...downloadContext,
          tags: { recovery_screen: "shown", recovery_reason: downloadFailed ? "page_download" : "crash" },
          extra: { componentStack: errorInfo.componentStack },
        });
      }
    } catch {
      // The recovery screen remains available even when reporting fails.
    }
  }

  componentDidUpdate(previousProps) {
    // React.lazy caches a rejected import, so clearing a download failure would
    // only rethrow it; keep the reload prompt until the page is reloaded.
    if (
      this.state.error &&
      previousProps.resetKey !== this.props.resetKey &&
      !isPageDownloadError(this.state.error)
    ) {
      this.setState({ error: null });
    }
  }

  retry = () => this.setState({ error: null });
  reload = () => {
    if (this.props.onReload) this.props.onReload();
    else window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (this.state.holding) return null;
    const downloadFailed = isPageDownloadError(this.state.error);
    return (
      <section className="boss-recovery" role="alert" aria-labelledby="boss-recovery-title">
        <div className="boss-recovery-card">
          <div className="boss-recovery-brand" aria-label="theBOSSapp">
            <span>the<span className="boss-recovery-brand-name">BOSS</span>app</span>
          </div>
          <svg className="boss-recovery-illustration" viewBox="0 0 240 160" fill="none" aria-hidden="true">
            <BossLogo className="boss-recovery-logo" x="48" y="8" width="144" height="144" />
            <circle cx="193" cy="42" r="6" fill="#e6b85c" />
            <circle cx="42" cy="119" r="4" fill="#9fb6c6" />
            <rect x="53" y="36" width="134" height="92" rx="12" fill="white" fillOpacity="0.65" stroke="#a9bdcb" strokeWidth="2" />
            <path d="M54 57h132" stroke="#d5e0e8" strokeWidth="2" />
            <circle cx="67" cy="47" r="3" fill="#9fb6c6" />
            <circle cx="78" cy="47" r="3" fill="#9fb6c6" />
            <circle cx="89" cy="47" r="3" fill="#9fb6c6" />
            <path d="M78 81h39M78 93h27M78 105h46" stroke="#c6d6e1" strokeWidth="5" strokeLinecap="round" />
            <circle cx="165" cy="113" r="26" fill="#002b49" stroke="white" strokeWidth="5" />
            <path d="M165 100v14" stroke="#f1c871" strokeWidth="4" strokeLinecap="round" />
            <circle cx="165" cy="123" r="2" fill="#f1c871" />
          </svg>
          <p className="boss-recovery-eyebrow">A small interruption</p>
          <h1 id="boss-recovery-title" ref={this.focusHeading} tabIndex={-1}>
            {downloadFailed ? "We couldn’t load this page" : "Something went wrong"}
          </h1>
          <p className="boss-recovery-description">
            {downloadFailed
              ? "Part of the app didn’t finish downloading. Check your connection, then reload to get a fresh copy."
              : "This page hit an unexpected problem. Try opening it again, or head back home to keep working."}
          </p>
          <div className="boss-recovery-actions">
            <button className="boss-recovery-primary" type="button" onClick={downloadFailed ? this.reload : this.retry}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M20 11a8 8 0 1 0-2.4 6.7M20 4v7h-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {downloadFailed ? "Reload page" : "Try again"}
            </button>
            <a className="boss-recovery-home" href="/home">Back to home <span aria-hidden="true">→</span></a>
          </div>
          <p className="boss-recovery-footnote">If you were editing, check your latest changes when the page opens.</p>
        </div>
      </section>
    );
  }
}

export default ErrorBoundary;
